import { ethers } from 'ethers';
import { useCallback, useEffect, useState } from 'react';
import getContract from '../../utils/getContract';
import {
  apiErrorMessage,
  fetchPerpetualDeposits,
  fetchPerpetualRate,
  fetchStakes,
} from '../../utils/api';
import ConfirmDialog from '../confirm/ConfirmDialog';
import Loader from '../loader/Loader';
import RateButton from '../rate/RateButton';
import Modal from '../modal/Modal';
import { useNotify } from '../notifications/NotificationsProvider';
import { useWallet } from '../wallet/WalletProvider';
import tokenAbi from '../../ABI/emivnAbi.json';
import stakingAbi from '../../ABI/stakingAbi.json';
import './SendTokens.css';
import {
  stakingAddress,
  tokenAddress,
  STAKING_RATES,
  getRateByDays,
  getStakePayout,
} from '../../config/config';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Два вида стейкинга — два набора полей и две функции контракта. */
const TERM = 'term';
const PERPETUAL = 'perpetual';

/**
 * Ждёт появления записи о стейкинге в реестре.
 *
 * Бэкенд синхронизируется с блокчейном с отставанием на несколько блоков и
 * циклом в 30 секунд, так что запись появляется в пределах минуты. Оператор
 * этого ожидания не ждёт — он закрывает попап и работает дальше, а опрос идёт
 * фоном и обновляет список, когда запись появится. Опрос идёт по списку — эта
 * ручка отвечает из накопленных данных и не обращается к блокчейну, поэтому
 * частые запросы ей безразличны.
 */
const waitForRecord = async (mode, receiver, txHash, timeoutMs = 90_000) => {
  const startedAt = Date.now();
  const address = receiver.toLowerCase();
  const hash = txHash.toLowerCase();

  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 5000));

    try {
      if (mode === PERPETUAL) {
        // Ищем в истории взносов, а не в списке позиций: позиция у адреса
        // могла существовать и до этой транзакции, и по ней не отличить
        // «долив записался» от «долив ещё не дошёл».
        const deposits = await fetchPerpetualDeposits({ address });

        if (
          deposits.some((deposit) => deposit.txHash?.toLowerCase() === hash)
        ) {
          return true;
        }
      } else {
        const { stakes } = await fetchStakes({
          receiver: address,
          claimed: false,
        });

        if (stakes.some((stake) => stake.hash?.toLowerCase() === hash)) {
          return true;
        }
      }
    } catch (error) {
      // Недоступность бэкенда не отменяет успешной транзакции — продолжаем
      // ждать до истечения таймаута.
      console.error('Опрос реестра не удался:', error.message);
    }
  }

  return false;
};

const SendTokens = ({ onStakeCreated, onRateChanged, refreshKey }) => {
  const {
    address,
    isReady,
    isConnected,
    isCorrectChain,
    isKnownAdmin,
    detecting,
  } = useWallet();
  const notify = useNotify();
  const [walletAddress, setWalletAddress] = useState('');
  const [tokensCount, setTokensCount] = useState('');
  const [term, setTerm] = useState(STAKING_RATES[0].days);
  const [mode, setMode] = useState(TERM);
  // Условия бессрочного приходят из контракта через бэкенд: ставка общая для
  // всех и меняется на лету, держать её копию в конфиге было бы враньём.
  const [perpetual, setPerpetual] = useState(null);
  const [perpetualError, setPerpetualError] = useState('');
  const [loading, setLoading] = useState(false);
  // Заявка на подпись: то, что оператор увидит в окне подтверждения и что
  // дословно уйдёт в кошелёк. Существует от нажатия кнопки до подтверждения
  // или отмены; отмена не оставляет следов, форма остаётся заполненной.
  const [request, setRequest] = useState(null);
  // Попап после успешной транзакции. Отдельно от уведомлений: карточка гаснет
  // сама, а здесь оператор должен именно прочитать, что записи в списке пока
  // нет и это нормально, — иначе отсутствие строки читается как потеря денег.
  const [stakeSentOpen, setStakeSentOpen] = useState(false);

  // Кнопки блокируются до подключения, а не показывают ошибку постфактум:
  // до этого момента отправка молча открывала кошелёк или падала на подписи.
  // Открытая заявка блокирует их же: пока окно подтверждения на экране, вторая
  // операция запускаться не должна.
  const disabled = loading || !isReady || Boolean(request);

  const loadPerpetual = useCallback(async () => {
    setPerpetualError('');

    try {
      const { apr, minStake } = await fetchPerpetualRate();
      setPerpetual({ apr, minStake: Number(minStake) });
    } catch (error) {
      console.error('Не удалось прочитать условия бессрочного:', error);
      setPerpetual(null);
      setPerpetualError(apiErrorMessage(error));
    }
  }, []);

  // refreshKey меняется и после смены ставки в окне условий: показывать
  // прежний APR в форме после этого нельзя.
  useEffect(() => {
    loadPerpetual();
  }, [loadPerpetual, refreshKey]);

  const closeStakeSent = () => {
    setStakeSentOpen(false);
    // Обновление на всякий случай: если оператор закрыл попап не сразу, запись
    // могла уже появиться.
    onStakeCreated?.();
  };

  const blockedReason = () => {
    if (detecting) return 'Поиск кошелька в браузере...';
    if (!isConnected) return 'Подключите кошелёк, чтобы отправлять транзакции.';
    if (!isCorrectChain) return 'Переключите кошелёк на сеть BSC.';
    return '';
  };

  const handleAddressChange = (e) => setWalletAddress(e.target.value);

  const handleTokensCountChange = (value) => {
    const cleaned = value.replace(/\D/g, '');
    setTokensCount(cleaned);
  };

  const handleTermChange = (e) => setTerm(Number(e.target.value));

  /**
   * Собирает заявку на стейкинг из полей формы или объясняет, почему нельзя.
   *
   * Проверки идут до окна подтверждения: в окне оператор должен видеть уже
   * проверенные значения, а не узнавать о неверном адресе после того, как
   * подтвердил отправку.
   */
  const buildStakeRequest = () => {
    if (!walletAddress || !ethers.isAddress(walletAddress)) {
      notify.error('Неправильный адрес кошелька');
      return null;
    }

    if (!tokensCount || Number(tokensCount) <= 0) {
      notify.error('Неверное количество токенов');
      return null;
    }

    const common = {
      mode,
      sender: ethers.getAddress(address),
      receiver: ethers.getAddress(walletAddress),
      amount: tokensCount,
    };

    if (mode === PERPETUAL) {
      if (!perpetual) {
        notify.error(
          'Условия бессрочного стейкинга не прочитаны — не с чем сверять минимум взноса.',
        );
        return null;
      }

      // Минимум проверяет и контракт, на каждом взносе, включая долив. Здесь
      // — чтобы оператор узнал об этом до того, как заплатит за ревёрт.
      if (Number(tokensCount) < perpetual.minStake) {
        notify.error(
          `Минимальный взнос — ${perpetual.minStake} EMIVN, контракт отклонит меньший.`,
        );
        return null;
      }

      if (!perpetual.apr) {
        // Ставка нулевая — взнос пройдёт, но проценты капать не будут, а тело
        // из контракта не достать. Молча пропускать такое нельзя.
        notify.error(
          'Ставка бессрочного стейкинга сейчас 0% — проценты капать не будут. ' +
            'Сначала установите APR — кнопка «Условия бессрочного стейкинга» над полями.',
        );
        return null;
      }

      return { ...common, apr: perpetual.apr };
    }

    const rate = getRateByDays(term);
    if (!rate) {
      notify.error('Неверный период стейкинга');
      return null;
    }

    return {
      ...common,
      term,
      rate,
      payout: getStakePayout(tokensCount, term),
      unlockAt: new Date(Date.now() + term * DAY_MS),
    };
  };

  const handleStakeClick = () => {
    const stakeRequest = buildStakeRequest();
    if (stakeRequest) setRequest(stakeRequest);
  };

  const handleConfirm = () => {
    const confirmed = request;
    setRequest(null);
    void sendTokens(confirmed);
  };

  const approveTokens = async () => {
    try {
      setLoading(true);
      const tokenContract = await getContract(tokenAddress, tokenAbi);
      const tx = await tokenContract.approve(
        stakingAddress,
        ethers.MaxUint256,
        { gasLimit: 250_000 },
      );
      await tx.wait();
      notify.success('Апрув успешно произведён');
    } catch (error) {
      console.error('Approval error:', error);

      if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
        notify.info('Апрув отклонён в кошельке');
        return;
      }

      notify.error(
        `Ошибка при апруве: ${error.shortMessage ?? error.message ?? 'причина неизвестна'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Отправляет ровно подтверждённую заявку.
   *
   * Параметры приходят аргументом, а не читаются из полей формы: иначе
   * обещание «подписывается то, что показано» нечем подкрепить — форма
   * остаётся на экране и продолжает жить своей жизнью.
   */
  const sendTokens = async (confirmed) => {
    const { receiver, amount: tokens, term: days, mode: kind } = confirmed;

    try {
      setLoading(true);
      const stakingContract = await getContract(stakingAddress, stakingAbi);

      const amount = ethers.parseEther(tokens);

      // Обе функции списывают токены с подписанта и записывают позицию на
      // `receiver`. Разница в том, что `stakeFor` требует ADMIN_ROLE, а
      // `stakePerpetualFor` — нет: в бессрочном роль на взнос не нужна.
      const tx =
        kind === PERPETUAL
          ? await stakingContract.stakePerpetualFor(receiver, amount)
          : await stakingContract.stakeFor(receiver, amount, days);

      const receipt = await tx.wait();

      if (receipt.status === 1) {
        setWalletAddress('');
        setTokensCount('');

        // Сообщать бэкенду о стейкинге не нужно: реестр — проекция блокчейна и
        // подхватывает событие сам, с задержкой до минуты. Держать оператора
        // под спиннером всё это время незачем — попап объясняет задержку, а
        // список догонит сам.
        setStakeSentOpen(true);

        // Фоном, без await: когда запись появится, список и балансы обновятся
        // сами. Перезагрузки страницы здесь нет — она сбрасывала подключение
        // кошелька и заставляла подключаться заново после каждой отправки.
        void waitForRecord(kind, receiver, tx.hash).then((appeared) => {
          if (appeared) {
            onStakeCreated?.();
          } else {
            notify.error(
              'Запись о стейкинге не появилась в реестре за полторы минуты. ' +
                'Транзакция в блокчейне прошла — проверьте синхронизацию бэкенда.',
            );
          }
        });
      }
    } catch (error) {
      console.error('TX error:', error);

      const revertData = error.data || error?.error?.data;
      if (revertData) {
        const tokenIface = new ethers.Interface(tokenAbi);
        try {
          const decoded = tokenIface.parseError(revertData);
          if (decoded.name === 'ERC20InsufficientBalance') {
            const balance = ethers.formatEther(decoded.args[1]);
            const needed = ethers.formatEther(decoded.args[2]);
            notify.error(
              `Недостаточно токенов. На балансе: ${balance}, требуется: ${needed}`,
            );
            return;
          }
          if (decoded.name === 'ERC20InsufficientAllowance') {
            notify.error(
              'Недостаточный апрув. Сначала выполните апрув для смарт контракта.',
            );
            return;
          }
        } catch {
          // не удалось декодировать — покажем общую ошибку ниже
        }
      }

      // Отказ в кошельке — не сбой, а решение оператора: отдельным текстом.
      if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
        notify.info('Транзакция отклонена в кошельке');
        return;
      }

      notify.error(
        `Ошибка в транзакции: ${error.shortMessage ?? error.message ?? 'причина неизвестна'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="send_tokens_container">
      <h3>ОТПРАВИТЬ ТОКЕНЫ</h3>

      {/* Переключатель, а не вторая форма рядом: поля совпадают на две трети,
          а две почти одинаковые формы на экране — это приглашение отправить
          не туда. Смена вида не сбрасывает адрес и сумму: оператор мог просто
          перепутать вид, и заставлять его перенабирать адрес незачем.

          Рядом — вход в условия бессрочного: они относятся к одному из видов,
          и место им там же, где вид выбирают. Показан он при любом виде:
          ставку меняют, не собираясь ничего отправлять. */}
      <div className="send_tokens__toolbar">
        <div className="mode_switch" role="group" aria-label="Вид стейкинга">
          <button
            type="button"
            className={mode === TERM ? 'mode_switch__active' : ''}
            onClick={() => setMode(TERM)}
            disabled={loading || Boolean(request)}
          >
            Срочный
          </button>
          <button
            type="button"
            className={mode === PERPETUAL ? 'mode_switch__active' : ''}
            onClick={() => setMode(PERPETUAL)}
            disabled={loading || Boolean(request)}
          >
            Бессрочный
          </button>
        </div>

        <RateButton refreshKey={refreshKey} onRateChanged={onRateChanged} />
      </div>

      <div className="inputs_container">
        <input
          type="text"
          placeholder="Адрес кошелька"
          value={walletAddress}
          onChange={handleAddressChange}
        />

        <input
          type="text"
          placeholder="Количество токенов"
          value={
            tokensCount
              ? new Intl.NumberFormat('ru-RU').format(tokensCount)
              : ''
          }
          onChange={(e) => handleTokensCountChange(e.target.value)}
        />

        {mode === TERM && (
          <select value={term} onChange={handleTermChange}>
            {STAKING_RATES.map(({ days, rate }) => (
              <option key={days} value={days}>
                {days} дней — {rate}%
              </option>
            ))}
          </select>
        )}
      </div>

      {/* У бессрочного нет ни срока, ни расчёта выплаты — вместо них условия
          одной строкой. «Тело возврату не подлежит» стоит здесь, а не только в
          окне подтверждения: это необратимо, и узнавать об этом на последнем
          шаге поздно. */}
      {mode === PERPETUAL && (
        <div className="perpetual_terms">
          {perpetualError && (
            <span className="perpetual_terms__error">
              Не удалось прочитать условия: {perpetualError}.{' '}
              <button type="button" onClick={loadPerpetual}>
                Повторить
              </button>
            </span>
          )}

          {!perpetualError && !perpetual && <span>Читаем условия...</span>}

          {perpetual && (
            <>
              <span>
                APR <b>{perpetual.apr}%</b> годовых, начисление посекундно, тело
                возврату не подлежит.
              </span>
              <span className="perpetual_terms__min">
                Минимальный взнос — {perpetual.minStake} EMIVN.
              </span>
            </>
          )}
        </div>
      )}

      <div className="buttons_container">
        {/* Без окна подтверждения, в отличие от стейкинга: сверять нечего —
            оператор не вводит ни адреса, ни суммы, все параметры заданы кодом. */}
        <button onClick={approveTokens} disabled={disabled}>
          Апрув для смарт контракта стейкинга
        </button>
        <button onClick={handleStakeClick} disabled={disabled}>
          {mode === PERPETUAL
            ? 'Отправить в бессрочный стейкинг'
            : 'Отправить в стейкинг'}
        </button>
      </div>

      {!isReady && <div className="send_notice">{blockedReason()}</div>}

      {/* Предупреждение, а не запрет: список админов в конфиге — справочная
          информация, право проверяет контракт. Но узнавать о чужом кошельке из
          ревёрта, уже потратив газ, дороже, чем прочитать строку. */}
      {isReady && !isKnownAdmin && mode === TERM && (
        <div className="send_warning">
          Подключённый адрес не значится в списке админов — контракт, скорее
          всего, отклонит стейкинг. Проверьте выбранный аккаунт в кошельке.
        </div>
      )}

      {/* У бессрочного взноса роли нет: `stakePerpetualFor` открыт всем, и
          контракт не отклонит транзакцию с чужого кошелька. Предупреждение про
          админов здесь было бы ложным, но токены-то спишутся с подключённого
          адреса — об этом и сообщаем. */}
      {isReady && !isKnownAdmin && mode === PERPETUAL && (
        <div className="send_warning">
          Подключённый адрес не значится в списке админов. Бессрочный взнос
          контракт всё равно примет — токены спишутся с этого кошелька.
          Проверьте выбранный аккаунт.
        </div>
      )}

      {loading && <Loader />}

      {request && (
        <ConfirmDialog
          request={request}
          onConfirm={handleConfirm}
          onCancel={() => setRequest(null)}
        />
      )}

      {stakeSentOpen && (
        <Modal title="Стейкинг отправлен" onClose={closeStakeSent}>
          <p>
            Транзакция подтверждена в блокчейне. Запись появится в реестре в
            течение минуты — список обновится сам, перезагружать страницу не
            нужно.
          </p>
          <div className="modal_actions">
            <button type="button" autoFocus onClick={closeStakeSent}>
              ОК
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SendTokens;
